import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  User, Bell, Settings, Briefcase, MapPin, Tag, CheckCircle2, Crown,
  FileText, Upload, Trash2, Lock, Edit2, Loader2, Gem, Star
} from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import axios from 'axios';
import { toast } from 'sonner';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Dashboard = ({ user: initialUser }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(initialUser);
  const [activeTab, setActiveTab] = useState('notifications');
  const [notifications, setNotifications] = useState([]);
  const [categories, setCategories] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [preferences, setPreferences] = useState({
    keywords: [],
    locations: [],
    domains: [],
    notification_channels: ['web']
  });
  const [newKeyword, setNewKeyword] = useState('');
  const [newLocation, setNewLocation] = useState('');
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(false);
  
  // Profile editing state
  const [profileData, setProfileData] = useState({
    nom: '',
    summary: '',
    phone: '',
    profession: ''
  });
  const [profileLoading, setProfileLoading] = useState(false);
  
  // CV state
  const [cvFile, setCvFile] = useState(null);
  const [cvUploading, setCvUploading] = useState(false);
  
  // Password change state
  const [passwordDialogOpen, setPasswordDialogOpen] = useState(false);
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Flag to prevent re-fetching after profile update
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);

  useEffect(() => {
    if (user && !isUpdatingProfile) {
      fetchNotifications();
      fetchPreferences();
      fetchCategories();
      fetchProfessions();
    }
  }, [user?.id]); // Only depend on user.id, not the entire user object

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data.categories || []);
    } catch (error) {
      console.error('Erreur catégories:', error);
    }
  };

  const fetchProfessions = async () => {
    try {
      const response = await axios.get(`${API}/professions`);
      setProfessions(response.data.professions || []);
    } catch (error) {
      console.error('Erreur professions:', error);
    }
  };

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
    } catch (error) {
      console.error('Erreur notifications:', error);
    }
  };

  const fetchPreferences = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users/profile`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPreferences(response.data.preferences);
      setProfileData({
        nom: response.data.nom || '',
        summary: response.data.summary || '',
        phone: response.data.phone || '',
        profession: response.data.profession || ''
      });
      // Update user with full data
      setUser(prev => ({ ...prev, ...response.data }));
    } catch (error) {
      console.error('Erreur préférences:', error);
    }
  };

  // Profile update
  const handleProfileUpdate = async () => {
    setProfileLoading(true);
    try {
      const token = localStorage.getItem('token');
      const dataToSend = {
        nom: profileData.nom,
        summary: profileData.summary,
        phone: profileData.phone,
        profession: profileData.profession
      };
      console.log('Sending profile update:', dataToSend);
      
      const response = await axios.put(`${API}/users/profile/update`, dataToSend, {
        headers: { Authorization: `Bearer ${token}` }
      });
      console.log('Update response:', response.data);
      
      toast.success('Profil mis à jour!');
      
      // Update local user immediately with sent data
      const updatedUser = { ...user, ...dataToSend };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
    } catch (error) {
      console.error('Profile update error:', error.response?.data || error);
      toast.error(error.response?.data?.detail || 'Erreur lors de la mise à jour du profil');
    } finally {
      setProfileLoading(false);
    }
  };

  // CV Upload
  const handleCvUpload = async () => {
    if (!cvFile) return;
    
    setCvUploading(true);
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', cvFile);
      
      const response = await axios.post(`${API}/users/cv/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success(response.data.message);
      setCvFile(null);
      fetchPreferences(); // Refresh to get new CV info
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload du CV');
    } finally {
      setCvUploading(false);
    }
  };

  // Delete CV
  const handleDeleteCv = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/users/cv`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('CV supprimé');
      setUser(prev => ({ ...prev, cv_url: null, cv_filename: null }));
    } catch (error) {
      toast.error('Erreur lors de la suppression du CV');
    }
  };

  // Password change
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Les mots de passe ne correspondent pas');
      return;
    }
    
    if (passwordData.newPassword.length < 6) {
      toast.error('Le nouveau mot de passe doit contenir au moins 6 caractères');
      return;
    }
    
    setPasswordLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/password`, {
        current_password: passwordData.currentPassword,
        new_password: passwordData.newPassword
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Mot de passe modifié avec succès!');
      setPasswordDialogOpen(false);
      setPasswordData({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors du changement de mot de passe');
    } finally {
      setPasswordLoading(false);
    }
  };

  // Get category icon
  const getCategoryIcon = (categoryId) => {
    switch (categoryId) {
      case 'diamond': return <Gem className="h-4 w-4" />;
      case 'gold': return <Star className="h-4 w-4" />;
      case 'premium': return <Crown className="h-4 w-4" />;
      default: return <User className="h-4 w-4" />;
    }
  };

  // Get category color
  const getCategoryStyle = (categoryId) => {
    switch (categoryId) {
      case 'diamond': return 'bg-gradient-to-r from-cyan-400 to-cyan-500 text-white';
      case 'gold': return 'bg-gradient-to-r from-yellow-400 to-yellow-500 text-slate-900';
      case 'premium': return 'bg-gradient-to-r from-amber-400 to-amber-500 text-slate-900';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/notifications/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const addItem = (type, value) => {
    if (!value.trim()) return;
    
    const key = type === 'keyword' ? 'keywords' : type === 'location' ? 'locations' : 'domains';
    if (preferences[key].includes(value.trim())) {
      toast.error('Déjà ajouté');
      return;
    }

    setPreferences({
      ...preferences,
      [key]: [...preferences[key], value.trim()]
    });

    if (type === 'keyword') setNewKeyword('');
    if (type === 'location') setNewLocation('');
    if (type === 'domain') setNewDomain('');
  };

  const removeItem = (type, value) => {
    const key = type === 'keyword' ? 'keywords' : type === 'location' ? 'locations' : 'domains';
    setPreferences({
      ...preferences,
      [key]: preferences[key].filter(item => item !== value)
    });
  };

  const toggleNotificationChannel = (channel) => {
    const channels = preferences.notification_channels.includes(channel)
      ? preferences.notification_channels.filter(c => c !== channel)
      : [...preferences.notification_channels, channel];
    
    setPreferences({ ...preferences, notification_channels: channels });
  };

  const savePreferences = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/users/profile`, preferences, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Préférences enregistrées!');
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setLoading(false);
    }
  };

  const handleUpgradePremium = async () => {
    // Rediriger vers la page de checkout avec les catégories disponibles
    navigate('/checkout');
  };

  const getDaysRemaining = () => {
    if (!user?.category_expires) return null;
    try {
      const expiry = new Date(user.category_expires);
      const now = new Date();
      const days = Math.ceil((expiry - now) / (1000 * 60 * 60 * 24));
      return days > 0 ? days : 0;
    } catch {
      return null;
    }
  };

  const getTimeAgo = (dateString) => {
    try {
      const date = new Date(dateString);
      return formatDistanceToNow(date, { addSuffix: true, locale: fr });
    } catch {
      return 'Récemment';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 
                className="text-3xl md:text-4xl font-bold text-slate-900 mb-2 tracking-tight" 
                style={{ fontFamily: 'Plus Jakarta Sans, sans-serif' }}
                data-testid="dashboard-title"
              >
                Tableau de bord
              </h1>
              <div className="flex items-center gap-3">
                <p className="text-slate-600">Bienvenue, {user?.nom} !</p>
                {user?.category_id && user.category_id !== 'free' && (
                  <Badge className={`font-semibold ${getCategoryStyle(user.category_id)}`}>
                    {getCategoryIcon(user.category_id)}
                    <span className="ml-1">{user.category_id.toUpperCase()}</span>
                  </Badge>
                )}
              </div>
              {getDaysRemaining() !== null && user?.category_id !== 'free' && (
                <p className="text-sm text-slate-500 mt-1">
                  Expire dans {getDaysRemaining()} jours
                </p>
              )}
            </div>
            {/* Subscription CTA — visible for everyone except Diamond */}
            {user?.category_id !== 'diamond' && (
              <Button
                onClick={handleUpgradePremium}
                className="bg-gradient-to-r from-yellow-400 to-yellow-500 hover:from-yellow-500 hover:to-yellow-600 text-slate-900 font-semibold shadow-md"
                data-testid="upgrade-premium-button"
              >
                <Crown className="h-4 w-4 mr-2" />
                {(!user?.category_id || user?.category_id === 'free')
                  ? 'Activer un abonnement'
                  : getDaysRemaining() !== null && getDaysRemaining() < 7
                    ? 'Renouveler mon abonnement'
                    : 'Améliorer mon plan'}
              </Button>
            )}
          </div>
        </div>

        {/* My Subscription card */}
        <div className="mb-8">
          <Card className="p-6 bg-gradient-to-br from-slate-900 to-slate-800 text-white border-0" data-testid="my-subscription-card">
            <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className={`h-14 w-14 rounded-xl flex items-center justify-center ${getCategoryStyle(user?.category_id || 'free')}`}>
                  {getCategoryIcon(user?.category_id || 'free')}
                </div>
                <div>
                  <p className="text-xs text-slate-300 uppercase tracking-wider">Mon abonnement</p>
                  <p className="text-2xl font-bold" data-testid="current-plan-name">
                    {!user?.category_id || user?.category_id === 'free'
                      ? 'Gratuit'
                      : user.category_id === 'premium' ? 'Premium'
                      : user.category_id === 'gold' ? 'Or'
                      : user.category_id === 'diamond' ? 'Diamant'
                      : user.category_id}
                  </p>
                  {getDaysRemaining() !== null && user?.category_id !== 'free' && (
                    <p className="text-sm text-slate-300 mt-1">
                      {getDaysRemaining() > 0
                        ? `Valide encore ${getDaysRemaining()} jour${getDaysRemaining() > 1 ? 's' : ''}`
                        : 'Expiré'}
                    </p>
                  )}
                  {(!user?.category_id || user?.category_id === 'free') && (
                    <p className="text-sm text-slate-300 mt-1">
                      Débloquez plus de notifications et de canaux (SMS, WhatsApp, Email).
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-col sm:flex-row gap-2 w-full md:w-auto">
                <Button
                  onClick={handleUpgradePremium}
                  variant="outline"
                  className="bg-white/10 border-white/30 text-white hover:bg-white/20 hover:text-white"
                  data-testid="manage-subscription-button"
                >
                  <Crown className="h-4 w-4 mr-2" />
                  Voir les plans
                </Button>
              </div>
            </div>
          </Card>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Notifications</p>
                <p className="text-3xl font-bold text-slate-900">{notifications.filter(n => !n.read).length}</p>
              </div>
              <div className="w-12 h-12 bg-sky-100 rounded-lg flex items-center justify-center">
                <Bell className="h-6 w-6 text-sky-600" />
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Mots-clés suivis</p>
                <p className="text-3xl font-bold text-slate-900">{preferences.keywords.length}</p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Tag className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </Card>
          <Card className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500 mb-1">Localisations</p>
                <p className="text-3xl font-bold text-slate-900">{preferences.locations.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <MapPin className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </Card>
        </div>

        {/* Main Content */}
        <Card className="p-6">
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-6">
              <TabsTrigger value="notifications" data-testid="notifications-tab">
                <Bell className="h-4 w-4 mr-2" />
                Notifications
              </TabsTrigger>
              <TabsTrigger value="profile" data-testid="profile-tab">
                <User className="h-4 w-4 mr-2" />
                Profil
              </TabsTrigger>
              <TabsTrigger value="preferences" data-testid="preferences-tab">
                <Settings className="h-4 w-4 mr-2" />
                Préférences
              </TabsTrigger>
            </TabsList>

            {/* Notifications Tab */}
            <TabsContent value="notifications" className="space-y-4">
              {notifications.length === 0 ? (
                <div className="text-center py-12" data-testid="no-notifications-message">
                  <Bell className="h-12 w-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-600">Aucune notification pour le moment</p>
                  <p className="text-sm text-slate-500 mt-2">Configurez vos préférences pour recevoir des alertes</p>
                </div>
              ) : (
                <div className="space-y-3" data-testid="notifications-list">
                  {notifications.map((notif) => (
                    <div
                      key={notif.id}
                      className={`p-4 rounded-lg border cursor-pointer transition-all ${
                        notif.read 
                          ? 'bg-white border-slate-200' 
                          : 'bg-sky-50 border-sky-200 hover:bg-sky-100'
                      }`}
                      onClick={() => {
                        if (!notif.read) markAsRead(notif.id);
                        navigate(`/jobs/${notif.job_id}`);
                      }}
                      data-testid={`notification-${notif.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <h4 className="font-semibold text-slate-900">{notif.titre}</h4>
                            {!notif.read && (
                              <span className="w-2 h-2 bg-sky-500 rounded-full"></span>
                            )}
                          </div>
                          <p className="text-sm text-slate-600">{notif.message}</p>
                          <p className="text-xs text-slate-500 mt-2">{getTimeAgo(notif.created_at)}</p>
                        </div>
                        {notif.read && (
                          <CheckCircle2 className="h-5 w-5 text-green-500" />
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            {/* Profile Tab */}
            <TabsContent value="profile" className="space-y-6">
              {/* Profile Information */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Edit2 className="h-5 w-5 text-sky-500" />
                  Informations personnelles
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="profile-nom">Nom complet</Label>
                    <Input
                      id="profile-nom"
                      value={profileData.nom}
                      onChange={(e) => setProfileData({ ...profileData, nom: e.target.value })}
                      className="mt-1"
                      data-testid="profile-name-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="profile-phone">Téléphone</Label>
                    <Input
                      id="profile-phone"
                      type="tel"
                      placeholder="+243..."
                      value={profileData.phone}
                      onChange={(e) => setProfileData({ ...profileData, phone: e.target.value })}
                      className="mt-1"
                      data-testid="profile-phone-input"
                    />
                  </div>
                </div>

                {/* Professional Profile Dropdown */}
                <div>
                  <Label htmlFor="profile-profession">Profil professionnel</Label>
                  {professions.length > 0 ? (
                    <Select
                      value={profileData.profession || undefined}
                      onValueChange={(value) => {
                        console.log('Selected profession:', value);
                        setProfileData(prev => ({ ...prev, profession: value }));
                      }}
                    >
                      <SelectTrigger className="mt-1" data-testid="profile-profession-select">
                        <SelectValue placeholder="Sélectionnez votre profession" />
                      </SelectTrigger>
                      <SelectContent className="max-h-[300px]">
                        {professions.map((profession) => (
                          <SelectItem key={profession} value={profession}>
                            {profession}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <div className="mt-1 h-9 flex items-center px-3 border rounded-md text-sm text-slate-500">
                      Chargement des professions...
                    </div>
                  )}
                </div>
                
                <div>
                  <Label htmlFor="profile-summary">Bio / À propos de moi</Label>
                  <Textarea
                    id="profile-summary"
                    placeholder="Décrivez votre parcours, vos compétences et ce que vous recherchez..."
                    value={profileData.summary}
                    onChange={(e) => setProfileData({ ...profileData, summary: e.target.value })}
                    className="mt-1"
                    rows={4}
                    data-testid="profile-summary-input"
                  />
                </div>
                
                <Button 
                  onClick={handleProfileUpdate}
                  disabled={profileLoading}
                  className="bg-sky-500 hover:bg-sky-600"
                  data-testid="save-profile-button"
                >
                  {profileLoading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer les modifications'
                  )}
                </Button>
              </div>

              {/* CV Upload Section */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <FileText className="h-5 w-5 text-green-500" />
                  CV / Curriculum Vitae
                </h3>
                
                {user?.cv_filename ? (
                  <div className="flex items-center justify-between p-4 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-3">
                      <FileText className="h-8 w-8 text-green-600" />
                      <div>
                        <p className="font-medium text-green-900">{user.cv_filename}</p>
                        <p className="text-sm text-green-700">CV uploadé avec succès</p>
                      </div>
                    </div>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleDeleteCv}
                      data-testid="delete-cv-button"
                    >
                      <Trash2 className="h-4 w-4 mr-1" />
                      Supprimer
                    </Button>
                  </div>
                ) : (
                  <div className="p-6 border-2 border-dashed border-slate-300 rounded-lg text-center">
                    <Upload className="h-10 w-10 text-slate-400 mx-auto mb-3" />
                    <p className="text-slate-600 mb-2">Téléchargez votre CV (PDF ou Word)</p>
                    <p className="text-sm text-slate-500 mb-4">Taille maximale: 5MB</p>
                    
                    <div className="flex items-center justify-center gap-3">
                      <Input
                        type="file"
                        accept=".pdf,.doc,.docx"
                        onChange={(e) => setCvFile(e.target.files[0])}
                        className="max-w-xs"
                        data-testid="cv-file-input"
                      />
                      {cvFile && (
                        <Button 
                          onClick={handleCvUpload}
                          disabled={cvUploading}
                          className="bg-green-500 hover:bg-green-600"
                          data-testid="upload-cv-button"
                        >
                          {cvUploading ? (
                            <>
                              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                              Upload...
                            </>
                          ) : (
                            <>
                              <Upload className="h-4 w-4 mr-2" />
                              Uploader
                            </>
                          )}
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                
                {user?.category_id === 'free' && (
                  <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm text-yellow-800">
                      <Crown className="h-4 w-4 inline mr-1" />
                      <strong>Conseil:</strong> Passez à Premium pour rendre votre CV visible par les recruteurs!
                    </p>
                  </div>
                )}
              </div>

              {/* Password Change Section */}
              <div className="border-t pt-6 space-y-4">
                <h3 className="text-lg font-semibold flex items-center gap-2">
                  <Lock className="h-5 w-5 text-red-500" />
                  Sécurité du compte
                </h3>
                
                <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-900">Mot de passe</p>
                    <p className="text-sm text-slate-600">Changez votre mot de passe régulièrement</p>
                  </div>
                  <Button
                    variant="outline"
                    onClick={() => setPasswordDialogOpen(true)}
                    data-testid="change-password-button"
                  >
                    <Lock className="h-4 w-4 mr-2" />
                    Changer
                  </Button>
                </div>
              </div>
            </TabsContent>

            {/* Preferences Tab */}
            <TabsContent value="preferences" className="space-y-6">
              {/* Keywords */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Mots-clés recherchés</Label>
                <p className="text-sm text-slate-600 mb-3">Ajoutez des mots-clés pour recevoir des notifications pour les emplois correspondants</p>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Ex: Développeur, Comptable..."
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addItem('keyword', newKeyword)}
                    data-testid="keyword-input"
                  />
                  <Button onClick={() => addItem('keyword', newKeyword)} data-testid="add-keyword-button">
                    Ajouter
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {preferences.keywords.map((keyword) => (
                    <Badge 
                      key={keyword} 
                      className="bg-sky-100 text-sky-700 hover:bg-sky-200 cursor-pointer"
                      onClick={() => removeItem('keyword', keyword)}
                      data-testid={`keyword-badge-${keyword}`}
                    >
                      {keyword} ×
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Locations */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Localisations préférées</Label>
                <p className="text-sm text-slate-600 mb-3">Sélectionnez les villes où vous souhaitez travailler</p>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Ex: Kinshasa, Lubumbashi..."
                    value={newLocation}
                    onChange={(e) => setNewLocation(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addItem('location', newLocation)}
                    data-testid="location-input"
                  />
                  <Button onClick={() => addItem('location', newLocation)} data-testid="add-location-button">
                    Ajouter
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {preferences.locations.map((location) => (
                    <Badge 
                      key={location} 
                      className="bg-purple-100 text-purple-700 hover:bg-purple-200 cursor-pointer"
                      onClick={() => removeItem('location', location)}
                      data-testid={`location-badge-${location}`}
                    >
                      {location} ×
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Domains */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Domaines d'activité</Label>
                <p className="text-sm text-slate-600 mb-3">Choisissez les secteurs qui vous intéressent</p>
                <div className="flex gap-2 mb-3">
                  <Input
                    placeholder="Ex: Technologie, Finance..."
                    value={newDomain}
                    onChange={(e) => setNewDomain(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && addItem('domain', newDomain)}
                    data-testid="domain-input"
                  />
                  <Button onClick={() => addItem('domain', newDomain)} data-testid="add-domain-button">
                    Ajouter
                  </Button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {preferences.domains.map((domain) => (
                    <Badge 
                      key={domain} 
                      className="bg-green-100 text-green-700 hover:bg-green-200 cursor-pointer"
                      onClick={() => removeItem('domain', domain)}
                      data-testid={`domain-badge-${domain}`}
                    >
                      {domain} ×
                    </Badge>
                  ))}
                </div>
              </div>

              {/* Notification Channels */}
              <div>
                <Label className="text-base font-semibold mb-3 block">Canaux de notification</Label>
                <p className="text-sm text-slate-600 mb-3">Choisissez comment vous souhaitez recevoir les notifications</p>
                <div className="space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="channel-web" 
                      checked={preferences.notification_channels.includes('web')}
                      onCheckedChange={() => toggleNotificationChannel('web')}
                      data-testid="channel-web-checkbox"
                    />
                    <Label htmlFor="channel-web" className="cursor-pointer">
                      Notifications web (Activé)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="channel-sms" 
                      checked={preferences.notification_channels.includes('sms')}
                      onCheckedChange={() => toggleNotificationChannel('sms')}
                      data-testid="channel-sms-checkbox"
                    />
                    <Label htmlFor="channel-sms" className="cursor-pointer">
                      SMS (Africa's Talking)
                    </Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox 
                      id="channel-whatsapp" 
                      checked={preferences.notification_channels.includes('whatsapp')}
                      onCheckedChange={() => toggleNotificationChannel('whatsapp')}
                      data-testid="channel-whatsapp-checkbox"
                    />
                    <Label htmlFor="channel-whatsapp" className="cursor-pointer">
                      WhatsApp
                    </Label>
                  </div>
                </div>
                
                {/* Phone Number Input for SMS/WhatsApp */}
                {(preferences.notification_channels.includes('sms') || 
                  preferences.notification_channels.includes('whatsapp')) && (
                  <div className="mt-4 p-4 bg-sky-50 rounded-lg border border-sky-200">
                    <Label className="text-sm font-semibold mb-2 block">Numéro de téléphone RDC</Label>
                    <p className="text-xs text-slate-600 mb-2">Format: +243 suivi de 9 chiffres</p>
                    <div className="flex gap-2">
                      <Input
                        type="tel"
                        placeholder="+243812345678"
                        value={preferences.phone || ''}
                        onChange={(e) => setPreferences({...preferences, phone: e.target.value})}
                        className="flex-1"
                        data-testid="phone-input"
                      />
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      Les notifications SMS utilisent Africa's Talking, optimisé pour la RDC.
                    </p>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <Button 
                onClick={savePreferences}
                disabled={loading}
                className="bg-sky-500 hover:bg-sky-600 text-white rounded-full px-8 py-6 font-semibold w-full md:w-auto"
                data-testid="save-preferences-button"
              >
                {loading ? 'Enregistrement...' : 'Enregistrer les préférences'}
              </Button>
            </TabsContent>
          </Tabs>
        </Card>
      </div>

      {/* Password Change Dialog */}
      <Dialog open={passwordDialogOpen} onOpenChange={setPasswordDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Lock className="h-5 w-5 text-red-500" />
              Changer le mot de passe
            </DialogTitle>
            <DialogDescription>
              Entrez votre mot de passe actuel et le nouveau mot de passe.
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handlePasswordChange} className="space-y-4">
            <div>
              <Label htmlFor="current-password">Mot de passe actuel</Label>
              <Input
                id="current-password"
                type="password"
                value={passwordData.currentPassword}
                onChange={(e) => setPasswordData({ ...passwordData, currentPassword: e.target.value })}
                required
                className="mt-1"
                data-testid="current-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="new-password">Nouveau mot de passe</Label>
              <Input
                id="new-password"
                type="password"
                value={passwordData.newPassword}
                onChange={(e) => setPasswordData({ ...passwordData, newPassword: e.target.value })}
                required
                className="mt-1"
                data-testid="new-password-input"
              />
            </div>
            
            <div>
              <Label htmlFor="confirm-new-password">Confirmer le nouveau mot de passe</Label>
              <Input
                id="confirm-new-password"
                type="password"
                value={passwordData.confirmPassword}
                onChange={(e) => setPasswordData({ ...passwordData, confirmPassword: e.target.value })}
                required
                className="mt-1"
                data-testid="confirm-new-password-input"
              />
            </div>
            
            <div className="flex gap-3 pt-2">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setPasswordDialogOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button 
                type="submit" 
                className="flex-1 bg-red-500 hover:bg-red-600"
                disabled={passwordLoading}
                data-testid="submit-password-change-button"
              >
                {passwordLoading ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Changement...
                  </>
                ) : (
                  'Changer le mot de passe'
                )}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
